import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, RouterLink, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { Observable, OperatorFunction, forkJoin, of } from 'rxjs';
import { catchError, filter, map, skip, take } from 'rxjs/operators';

import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { ServerStatusService } from '@app/common/server-status.service';
import { ValuesService } from '@app/common/values/values.service';
import { HrService } from '@app/hr/hr.service';
import { AlertService } from '@app/shared/alert.service';
import { C_PAYMENT_METHOD, HABILITATION_ADMIN, HABILITATION_HR_MANAGER, HABILITATION_SAFE } from '@app/shared/constants';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SpinnerService } from '@app/shared/spinner.service';
import { businessDayOf, parseTz, pickedDateStr } from '@app/shared/time';
import { C_value, Station, Store, User } from '@app/shared/_models/common';
import { hrHabilitationsOf } from '@app/shared/_models/hr';
import { CHANNEL_FILTERS, CHANNEL_LABELS, CashFamily, CashTransfersDay, ChequeRemittance, DayPayment, DayPayments, MethodOption, MethodTotal, NATURE_LABELS, PaymentChannel, PaymentNature, DEFAULT_CHANNELS, SAFE_RESERVED_HINT, SafeBalance, SafesList, cashFamilyOf, countStamp, dayTimeStamp, isPosStation, isSafeStation, lostCause, lostReply, mergeCashTotals, methodLabel, methodOptionsOf, refusalText, sumCents } from './reglement.models';
import { csvFileName, downloadCsv, paymentsCsv, transfersCsv } from './reglement.csv';
import { CashGapsComponent } from './cash-gaps/cash-gaps.component';
import { ChequeRemittanceOutcome, ReglementService } from './reglement.service';
import { ChequeRemittanceDialogComponent, ChequeRemittanceDialogData } from './cheque-remittance-dialog/cheque-remittance-dialog.component';
import { TransferDialogComponent, TransferDialogData, TransferDialogResult } from './transfer-dialog/transfer-dialog.component';
import { StrongboxCountDialogComponent, StrongboxCountDialogData } from './strongbox-count-dialog/strongbox-count-dialog.component';

type Outcome<T> = { data: T; error: '' } | { data: null; error: string };

// Chaque lecture garde son refus pour elle : un échec des mouvements ne masque pas les règlements.
function outcome<T>(): OperatorFunction<T, Outcome<T>> {
	return (src: Observable<T>) => src.pipe(
		map((data): Outcome<T> => ({ data, error: '' })),
		catchError((e) => of<Outcome<T>>({ data: null, error: refusalText(e) })),
	);
}

// Paramètres d'URL illisibles : ignorés un par un, jamais d'erreur (jour réel AAAA-MM-JJ, id entier sur 32 bits).
function urlDay(v: string | null): string | null {
	return v && /^\d{4}-\d{2}-\d{2}$/.test(v) && dayjs(v).isValid() && dayjs(v).format('YYYY-MM-DD') === v ? v : null;
}

function urlInt(v: string, signed: boolean): number | null {
	const n = (signed ? /^-?\d{1,10}$/ : /^\d{1,10}$/).test(v) ? Number(v) : NaN;
	return Number.isFinite(n) && Math.abs(n) <= 2147483647 && (signed || n > 0) ? n : null;
}

function urlIds(v: string | null): number[] | null {
	const ids = [...new Set((v ?? '').split(',').map((x) => urlInt(x.trim(), true)).filter((n): n is number => n !== null))];
	return ids.length ? ids : null;
}

// Absent : null, les cases par défaut ; vide : aucune case ; canal inconnu ou reprise ignorés un par un.
function urlChannels(v: string | null): PaymentChannel[] | null {
	if (v === null) return null;
	const asked = new Set(v.split(',').map((x) => x.trim()));
	return CHANNEL_FILTERS.filter((c) => asked.has(c));
}

@Component({
	selector: 'app-reglement',
	standalone: true,
	templateUrl: './reglement.component.html',
	styleUrls: ['./reglement.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MenuCommonComponent, SelectDateComponent, CashGapsComponent, FormsModule, RouterLink, MatButtonModule, MatButtonToggleModule, MatCheckboxModule, MatIconModule, MatTooltipModule, FormatCurrencyPipe],
})
export class ReglementComponent implements OnInit {

	private reglementService = inject(ReglementService);
	private commonService = inject(CommonService);
	private valuesService = inject(ValuesService);
	private hrService = inject(HrService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private dialog = inject(MatDialog);
	private spin = inject(SpinnerService);
	private serverStatus = inject(ServerStatusService);
	private destroyRef = inject(DestroyRef);
	private fc = new FormatCurrencyPipe();
	private route = inject(ActivatedRoute);
	private router = inject(Router);

	// En journée, `date` est le jour ; en période, `date` et `dateTo` sont les jours métier de début et de fin, inclus.
	public periodMode = false;
	public date: Date = new Date();
	public dateTo: Date = new Date();
	// Tant que l'utilisateur n'a pas choisi de jour, le serveur fixe le jour métier courant (bascule de c_values 1201).
	private dateFromServer = true;
	// Absent de l'URL : magasin du poste courant (« all » = Tous).
	private readonly defaultStore: number | null = this.loginService.currentstation?.store ?? null;
	public store: number | null = this.defaultStore;
	public station: number | null = null;
	// Ids envoyés tels quels : la famille espèces part d'un bloc (« 2,-1 »).
	public method: number[] | null = null;
	// Cases cochées dans l'ordre de CHANNEL_FILTERS ; [] = aucune, seule la reprise reste.
	public channels: PaymentChannel[] = [...DEFAULT_CHANNELS];
	public stores: Store[] = [];
	public stations: Station[] = [];
	public methodOptions: MethodOption[] = [];
	public cashFamily: CashFamily | null = null;
	// Référentiels arrivés : avant, un paramètre d'URL n'est contrôlé que dans sa forme.
	private storesLoaded = false;
	private stationsLoaded = false;
	private methodsLoaded = false;
	private started = false;
	// Navigations lancées par syncUrl : leur écho (même dépassé par une suivante) ne doit pas réappliquer un état périmé.
	private ownWrites = 0;
	// Habilitations lues dans GET /users (la base fait foi) ; tant qu'elles ne sont pas lues, ni « Nouveau mouvement » ni dépôt.
	public isAdmin = false;
	public hasSafeRight = false;
	// Carte « Écarts de caisse » : donnée RH, montée pour l'habilitation 1 seule (le serveur refuse les autres).
	public isHrManager = false;

	public day: DayPayments | null = null;
	// Totaux du serveur, la famille espèces fusionnée en une ligne.
	public totals: MethodTotal[] = [];
	public transfers: CashTransfersDay | null = null;
	public safes: SafeBalance[] | null = null;
	// Sans l'habilitation Coffres-forts, le serveur rend des soldes null : « Réservé », total masqué.
	public safesVisible = false;
	// Filtres de la lecture affichée : ce sont eux que nomment le fichier exporté et l'impression.
	private shownStore: number | null = null;
	private shownStation: number | null = null;
	private shownMethod: number[] | null = null;
	private shownChannels: PaymentChannel[] = [...DEFAULT_CHANNELS];
	public paymentsError = '';
	public transfersError = '';
	public safesError = '';
	// Remises de chèques de la période et du magasin affichés (F) ; le poste n'y est pas un filtre.
	public remittances: ChequeRemittance[] | null = null;
	public remittancesError = '';
	public loading = false;
	private loadToken = 0;
	private strongboxToken = 0;

	readonly channelLabels = CHANNEL_LABELS;
	readonly channelFilters = CHANNEL_FILTERS;
	readonly reservedHint = SAFE_RESERVED_HINT;
	readonly sameIds = (a: number[] | null, b: number[] | null) => (a?.join(',') ?? null) === (b?.join(',') ?? null);
	// Un règlement de reprise ou de lettrage porte jusqu'à des centaines de pièces : au-delà de 3, « +N » à déplier (le CSV garde tout).
	readonly ordersShown = 3;
	private expandedOrders = new Set<number>();

	ngOnInit(): void {
		// L'URL est la seule source des filtres : lue à l'ouverture, relue si elle change sous l'onglet ouvert (lien du menu).
		this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => this.applyUrl(q));
		this.commonService.getStores().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (stores) => { this.stores = stores ?? []; this.storesLoaded = true; this.reconcile(); },
			error: (e) => console.error('[ReglementComponent] magasins indisponibles', e),
		});
		this.commonService.getStations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (stations) => {
				this.stations = [...(stations ?? [])].sort((a, b) => (a.store ?? 0) - (b.store ?? 0) || (a.id ?? 0) - (b.id ?? 0));
				this.stationsLoaded = true;
				this.reconcile();
			},
			error: (e) => console.error('[ReglementComponent] postes indisponibles', e),
		});
		// Flux vivant du cache (jamais terminé) : abonnement à part, jamais dans le forkJoin des lectures.
		// Modes inactifs retirés du sélecteur (JP, 03/10) ; leurs règlements passés gardent le nom servi par le serveur.
		this.valuesService.getValues$(C_PAYMENT_METHOD).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (values: C_value[]) => {
				this.cashFamily = cashFamilyOf(values);
				this.methodOptions = methodOptionsOf(values, this.cashFamily);
				this.methodsLoaded = true;
				this.regroup();
				this.reconcile();
			},
			error: (e) => console.error('[ReglementComponent] modes de règlement indisponibles', e),
		});
		// Liste en échec : repli sur les habilitations du jeton (hrHabilitationsOf le fait sur une liste vide).
		this.hrService.getUsers$().pipe(take(1), catchError(() => of([] as User[])), takeUntilDestroyed(this.destroyRef))
			.subscribe((users) => {
				const habilitations = hrHabilitationsOf(this.loginService.loggeduser, users);
				this.isAdmin = habilitations.includes(HABILITATION_ADMIN);
				this.hasSafeRight = habilitations.includes(HABILITATION_SAFE);
				this.isHrManager = habilitations.includes(HABILITATION_HR_MANAGER);
			});
		// Mouvement (bouton de caisse, « Nouveau mouvement ») ou remise de chèques : l'onglet se relit sans voile, la boîte de dialogue reste lisible.
		this.reglementService.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load(true));
		this.reglementService.remittancesChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load(true));
		this.reglementService.strongboxCountsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reloadStrongboxes());
		// Retour de la connexion (et départ de toute requête après une coupure : relecture en trop, sans risque) : une écriture à
		// réponse perdue a pu passer, et la relecture faite pendant la coupure a échoué.
		this.serverStatus.isOnline$.pipe(skip(1), filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load(true));
		this.started = true;
		this.syncUrl();
		this.load();
	}

	private applyUrl(q: ParamMap): void {
		if (this.ownWrites) return;
		const before = this.filterKey();
		this.readUrl(q);
		this.normalize();
		if (!this.started) return;
		this.syncUrl();
		if (this.filterKey() !== before) this.load();
	}

	// `from`/`to` présents : période (une fin avant le début suit le début) ; sinon `date` ; sinon le serveur fixe le jour.
	private readUrl(q: ParamMap): void {
		const from = urlDay(q.get('from')), to = urlDay(q.get('to'));
		const start = from ?? to ?? urlDay(q.get('date'));
		this.periodMode = !!(from || to);
		this.dateFromServer = !start;
		if (start) {
			const end = this.periodMode && to && to > start ? to : start;
			if (pickedDateStr(this.date) !== start) this.date = dayjs(start).toDate();
			if (pickedDateStr(this.dateTo) !== end) this.dateTo = dayjs(end).toDate();
		}
		const store = q.get('store');
		this.store = store === 'all' ? null : (store !== null && urlInt(store, false)) || this.defaultStore;
		const station = q.get('station');
		this.station = station === null ? null : urlInt(station, false);
		const method = urlIds(q.get('method'));
		if (!this.sameIds(method, this.method)) this.method = method;
		const channels = urlChannels(q.get('channel')) ?? DEFAULT_CHANNELS;
		if (channels.join(',') !== this.channels.join(',')) this.channels = [...channels];
	}

	// Contrôle contre les référentiels arrivés : magasin inconnu → celui du poste, poste hors magasin → Tous, modes → une option.
	private normalize(): void {
		if (this.storesLoaded && this.store !== null && !this.stores.some((s) => s.id === this.store)) this.store = this.defaultStore;
		const st = this.station === null ? undefined : this.stations.find((s) => s.id === this.station);
		if (this.stationsLoaded && this.station !== null && (!st || (this.store !== null && st.store !== this.store))) this.station = null;
		if (this.methodsLoaded && this.method) {
			const ids = this.optionFor(this.method)?.ids ?? null;
			if (!this.sameIds(ids, this.method)) this.method = ids && [...ids];
		}
	}

	// Une seule option désignée (un membre de la famille espèces vaut la famille) ; plusieurs, ou aucune : paramètre ignoré.
	private optionFor(ids: number[]): MethodOption | null {
		const family = this.cashFamily;
		const hits = new Set(ids.map((id) => family?.ids.includes(id)
			? this.methodOptions.find((o) => o.ids.some((x) => family.ids.includes(x)))
			: this.methodOptions.find((o) => o.ids.length === 1 && o.ids[0] === id)).filter((o) => !!o));
		return hits.size === 1 ? [...hits][0]! : null;
	}

	private reconcile(): void {
		const before = this.filterKey();
		this.normalize();
		if (this.started && this.filterKey() !== before) this.refilter();
	}

	// Seule forme écrite, donc celle d'un lien copié : magasin toujours explicite, jour absent tant que le serveur ne l'a pas fixé.
	private queryOf(): Record<string, string> {
		const q: Record<string, string> = {};
		if (!this.dateFromServer) {
			if (this.periodMode) {
				q['from'] = pickedDateStr(this.date) ?? '';
				q['to'] = pickedDateStr(this.dateTo) ?? '';
			} else {
				q['date'] = pickedDateStr(this.date) ?? '';
			}
		}
		q['store'] = this.store === null ? 'all' : String(this.store);
		if (this.station !== null) q['station'] = String(this.station);
		if (this.method?.length) q['method'] = this.method.join(',');
		// Omis au défaut (Comptoir seul), comme « Tous » les modes ; vide = aucune case.
		if (this.channels.join(',') !== DEFAULT_CHANNELS.join(',')) q['channel'] = this.channels.join(',');
		return q;
	}

	private filterKey(): string {
		return JSON.stringify(this.queryOf());
	}

	// replaceUrl : aucun filtre n'ajoute d'entrée d'historique, Retour ramène à la page d'avant l'onglet.
	private syncUrl(): void {
		const queryParams = this.queryOf();
		const cur = this.route.snapshot.queryParamMap;
		const keys = Object.keys(queryParams);
		if (cur.keys.length === keys.length && keys.every((k) => cur.get(k) === queryParams[k])) return;
		this.ownWrites++;
		this.router.navigate([], { queryParams, replaceUrl: true })
			.catch((e) => console.error('[ReglementComponent] URL des filtres non écrite', e))
			.finally(() => this.ownWrites--);
	}

	private refilter(): void {
		this.syncUrl();
		this.load();
	}

	private regroup(): void {
		this.totals = this.day ? mergeCashTotals(this.day.totals, this.cashFamily) : [];
	}

	// Mouvement libre : Admin, hors caisse et hors coffre ; sur une caisse le serveur ne laisse que le versement au coffre, le dépôt et l'approvisionnement depuis un coffre.
	get canTransfer(): boolean {
		return this.offCounter && this.isAdmin;
	}

	// Dépôt en banque depuis un coffre : Admin ou habilitation Coffres-forts, depuis tout poste sauf un coffre (l'utilisateur décide, pas le poste).
	get canDeposit(): boolean {
		const st = this.loginService.currentstation;
		return !!st?.id && !isSafeStation(st) && (this.isAdmin || this.hasSafeRight);
	}

	// Remise de chèques (créer, annuler) : mêmes droits que le dépôt, côté serveur aussi (check_author de remittance.rs).
	get canRemit(): boolean {
		return this.canDeposit;
	}

	// Coffre → caisse du même magasin (monnaie du matin) : mêmes droits que le dépôt, caisse comprise (RegisterRefill de check_register_rule, JP 04/10).
	get canRefill(): boolean {
		return this.canDeposit;
	}

	// Inventaire d'un coffre : habilitation Coffres-forts seule (le solde d'un coffre est sa donnée), jamais l'Admin seul ; pas depuis un coffre, comme le dépôt.
	get canCountStrongbox(): boolean {
		const st = this.loginService.currentstation;
		return !!st?.id && !isSafeStation(st) && this.hasSafeRight;
	}

	private get offCounter(): boolean {
		const st = this.loginService.currentstation;
		return !!st?.id && !isPosStation(st) && !isSafeStation(st);
	}

	// Postes du magasin filtré (tous les postes si « Tous »), coffres compris : ils portent les mouvements d'espèces.
	get stationOptions(): Station[] {
		return this.store === null ? this.stations : this.stations.filter((s) => s.store === this.store);
	}

	stationLabel(s: Station): string {
		const name = s.name ?? 'Poste ' + s.id;
		return this.store === null ? `${name} — ${this.stores.find((st) => st.id === s.store)?.name ?? 'magasin ' + s.store}` : name;
	}

	load(quiet = false): void {
		const token = ++this.loadToken;
		const from = this.dateFromServer ? null : pickedDateStr(this.date);
		const to = this.periodMode && from ? pickedDateStr(this.dateTo) : null;
		const store = this.store;
		const station = this.station;
		const method = this.method;
		const channel = this.channels;
		this.loading = true;
		const reads$ = forkJoin({
			payments: this.reglementService.getDayPayments$({ from, to, store, station, method, channel }).pipe(outcome()),
			transfers: this.reglementService.getTransfers$({ from, to, store, station }).pipe(outcome()),
			safes: this.reglementService.getSafes$(store).pipe(outcome()),
			remittances: this.reglementService.getChequeRemittances$({ from, to, store }).pipe(outcome()),
		});
		(quiet ? reads$ : reads$.pipe(this.spin.block('Chargement des règlements…'))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ payments, transfers, safes, remittances }) => {
			if (token !== this.loadToken) return;
			this.loading = false;
			this.shownStore = store;
			this.shownStation = station;
			this.shownMethod = method;
			this.shownChannels = channel;
			this.day = payments.data;
			this.regroup();
			this.paymentsError = payments.error;
			this.transfers = transfers.data;
			this.transfersError = transfers.error;
			this.showStrongboxes(safes);
			this.remittances = remittances.data?.remittances ?? null;
			this.remittancesError = remittances.error;
			const served = payments.data?.date ?? transfers.data?.date;
			if (this.dateFromServer && served) {
				this.date = dayjs(served).toDate();
				this.dateTo = this.date;
				this.dateFromServer = false;
				this.syncUrl();
			}
		});
	}

	// Un inventaire ne change que les coffres : seule leur carte est relue, sans voile. Une lecture complète en cours a pu les lire avant lui : elle est relancée.
	private reloadStrongboxes(): void {
		if (this.loading) { this.load(true); return; }
		const token = this.loadToken, strongboxToken = ++this.strongboxToken;
		this.reglementService.getSafes$(this.shownStore).pipe(outcome(), takeUntilDestroyed(this.destroyRef)).subscribe((safes) => {
			if (token === this.loadToken && strongboxToken === this.strongboxToken) this.showStrongboxes(safes);
		});
	}

	private showStrongboxes(safes: Outcome<SafesList>): void {
		this.safes = safes.data?.safes ?? null;
		this.safesVisible = safes.data?.balances_visible === true;
		this.safesError = safes.error;
	}

	// Jour, ou début de période : une fin antérieure suit le début.
	dateChange(d: any): void {
		if (!pickedDateStr(d)) { this.date = new Date(this.date); return; }
		this.date = d;
		if (this.periodMode && (pickedDateStr(this.dateTo) ?? '') < (pickedDateStr(d) ?? '')) this.dateTo = d;
		this.dateFromServer = false;
		this.refilter();
	}

	// Fin de période : un début postérieur suit la fin.
	dateToChange(d: any): void {
		if (!pickedDateStr(d)) { this.dateTo = new Date(this.dateTo); return; }
		this.dateTo = d;
		if ((pickedDateStr(this.date) ?? '') > (pickedDateStr(d) ?? '')) this.date = d;
		this.dateFromServer = false;
		this.refilter();
	}

	// Une journée est une période d'un jour : basculer sans changer de fenêtre ne relit rien (ni requête, ni voile).
	// Retour d'une période de plusieurs jours : relecture, les totaux du serveur couvrent toute la période et la liste peut être tronquée.
	setPeriodMode(on: boolean): void {
		if (on === this.periodMode) return;
		const sameWindow = !this.dateFromServer && pickedDateStr(this.date) === pickedDateStr(this.dateTo);
		this.periodMode = on;
		if (on) this.dateTo = this.date;
		if (on ? !this.dateFromServer : sameWindow) { this.syncUrl(); return; }
		this.dateFromServer = false;
		this.refilter();
	}

	setStore(store: number | null): void {
		this.store = store;
		if (this.station !== null && store !== null && this.stations.find((s) => s.id === this.station)?.store !== store) this.station = null;
		this.refilter();
	}

	setStation(station: number | null): void {
		this.station = station;
		this.refilter();
	}

	// `ids` : ceux d'une option du sélecteur (la famille espèces entière pour « Espèces »).
	setMethod(ids: number[] | null): void {
		this.method = ids?.length ? [...ids] : null;
		this.refilter();
	}

	hasChannel(c: PaymentChannel): boolean {
		return this.channels.includes(c);
	}

	toggleChannel(c: PaymentChannel, on: boolean): void {
		this.channels = CHANNEL_FILTERS.filter((x) => x === c ? on : this.channels.includes(x));
		this.refilter();
	}

	openTransfer(): void {
		if (!this.canTransfer) return;
		this.openTransferDialog({ mode: 'free', store: this.loginService.currentstation?.store ?? null });
	}

	// `safe` : bouton d'un coffre de la carte ; sinon le coffre du magasin filtré (du poste courant si « Tous ») est proposé.
	openDeposit(safe?: SafeBalance): void {
		if (!this.canDeposit) return;
		this.openTransferDialog(safe
			? { mode: 'deposit', store: safe.store, source: safe.station }
			: { mode: 'deposit', store: this.store ?? this.loginService.currentstation?.store ?? null });
	}

	// Bouton d'un coffre de la carte (la barre est pleine à 1 880 px) ; depuis une caisse du même magasin, elle est proposée en destination.
	openRefill(safe: SafeBalance): void {
		if (!this.canRefill) return;
		const st = this.loginService.currentstation;
		const own = isPosStation(st) && st!.store === safe.store;
		this.openTransferDialog({ mode: 'refill', store: safe.store, source: safe.station, destination: own ? st!.id : null });
	}

	// Succès affiché dans la boîte (phrase du serveur), relecture par strongboxCountsChanged$ ; même garde du double clic et du Retour que les autres boîtes.
	openStrongboxCount(strongbox: SafeBalance): void {
		if (!this.canCountStrongbox || this.dialog.openDialogs.length) return;
		this.dialog.open<StrongboxCountDialogComponent, StrongboxCountDialogData>(StrongboxCountDialogComponent, { data: { strongbox }, maxWidth: '96vw', autoFocus: 'dialog', closeOnNavigation: false });
	}

	// Le rechargement passe par changed$ : ici, seulement la phrase du serveur. Un second clic rapide n'empile pas une seconde boîte ;
	// closeOnNavigation : le Retour du navigateur détruirait la boîte et couperait un mouvement peut-être enregistré.
	private openTransferDialog(data: TransferDialogData): void {
		if (this.dialog.openDialogs.length) return;
		this.dialog.open<TransferDialogComponent, TransferDialogData, TransferDialogResult>(TransferDialogComponent, { data, maxWidth: '96vw', autoFocus: 'dialog', closeOnNavigation: false })
			.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((r) => {
				if (r) this.alertService.success(r.msg);
			});
	}

	// Jours métier de la lecture affichée (servis par le serveur) : ceux du portefeuille de chèques et des bordereaux.
	private get shownWindow(): { from: string; to: string } | null {
		const w = this.day ?? this.transfers;
		return w ? { from: w.date, to: w.date_to || w.date } : null;
	}

	// Le portefeuille a son propre prédicat (chèques non remis, comptoir ou reprise) : ni le mode ni le canal affichés n'y comptent.
	get canPrintCheques(): boolean {
		return !!this.shownWindow;
	}

	openChequeRemittance(): void {
		const w = this.shownWindow;
		if (!w || this.dialog.openDialogs.length) return;
		const data: ChequeRemittanceDialogData = { ...w, store: this.shownStore, station: this.shownStation, stores: this.stores, stations: this.stations, canRemit: this.canRemit };
		this.dialog.open<ChequeRemittanceDialogComponent, ChequeRemittanceDialogData, ChequeRemittanceOutcome>(ChequeRemittanceDialogComponent, { data, maxWidth: '96vw', autoFocus: 'dialog', closeOnNavigation: false });
	}

	// Filtres de la lecture affichée : on imprime ce que l'écran montre, jour métier servi par le serveur compris.
	printPayments(): void {
		if (!this.day?.payments.length) return;
		this.print(this.reglementService.printPayments$({ from: this.day.date, to: this.day.date_to, store: this.shownStore, station: this.shownStation, method: this.shownMethod, channel: this.shownChannels }));
	}

	printRemittance(r: ChequeRemittance): void {
		this.print(this.reglementService.printChequeRemittance$(r.remittance));
	}

	// Les chèques de la remise reviennent à remettre ; la carte se relit par remittancesChanged$.
	// Sans takeUntilDestroyed : l'annulation confirmée part même si l'onglet a été quitté pendant la question (le service est global).
	cancelRemittance(r: ChequeRemittance): void {
		if (!this.canRemit) return;
		Swal.fire({
			title: `Annuler la remise n° ${r.remittance} ?`,
			text: `${r.count} chèque${r.count > 1 ? 's' : ''}, ${this.fc.transform(r.total)} € à ${r.bank_name ?? 'la banque ' + r.bank} : ils reviendront dans les chèques à remettre.`,
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Annuler la remise', cancelButtonText: 'Garder la remise',
		}).then((res) => {
			if (!res.isConfirmed) return;
			this.reglementService.cancelChequeRemittance$(r.remittance).pipe(this.spin.block('Annulation de la remise…')).subscribe({
				next: (resp) => this.alertService.success(resp.msg || `Remise n° ${r.remittance} annulée.`),
				error: (e) => lostReply(e)
					? this.alertService.warn(`Réponse perdue (${lostCause(e)}) : l'annulation de la remise n° ${r.remittance} a peut-être été faite. La carte des remises est relue : vérifiez-la avant de recommencer.`)
					: this.alertService.error('Annulation refusée : ' + refusalText(e)),
			});
		});
	}

	// Bordereau d'un dépôt déjà enregistré ; le dépôt lui-même (montant saisi) passe par « Dépôt en banque ».
	printDeposit(transfer: number): void {
		this.print(this.reglementService.printDeposit$(transfer));
	}

	printTransfers(): void {
		if (!this.transfers?.transfers.length) return;
		this.print(this.reglementService.printTransfers$({ from: this.transfers.date, to: this.transfers.date_to, store: this.shownStore, station: this.shownStation }));
	}

	// Appelé dans le clic : le service y ouvre la fenêtre d'impression avant tout appel réseau.
	private print(print$: Observable<void>): void {
		print$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			error: (e) => this.alertService.error('Impression impossible : ' + refusalText(e)),
		});
	}

	exportPayments(): void {
		if (!this.day?.payments.length) return;
		downloadCsv(csvFileName('reglements', this.day.date, this.day.date_to, this.shownStoreRef(), this.shownStationRef()), paymentsCsv(this.day.payments, this.cashFamily));
		if (this.day.truncated) this.alertService.warn(`Fichier limité aux ${this.day.payments.length} règlements listés : ${this.multiDay ? 'la période' : 'la journée'} en compte ${this.day.count}.`);
	}

	exportTransfers(): void {
		if (!this.transfers?.transfers.length) return;
		downloadCsv(csvFileName('mouvements-especes', this.transfers.date, this.transfers.date_to, this.shownStoreRef(), this.shownStationRef()), transfersCsv(this.transfers.transfers));
	}

	private shownStoreRef(): { id: number; name?: string | null } | null {
		if (this.shownStore === null) return null;
		return { id: this.shownStore, name: this.stores.find((s) => s.id === this.shownStore)?.name };
	}

	private shownStationRef(): { id: number; name?: string | null } | null {
		if (this.shownStation === null) return null;
		return { id: this.shownStation, name: this.stations.find((s) => s.id === this.shownStation)?.name };
	}

	// Dernier jour choisi dans la barre : la carte des écarts propose son mois à sa première ouverture.
	get lastDay(): string | null {
		return pickedDateStr(this.periodMode ? this.dateTo : this.date);
	}

	// La lecture affichée couvre-t-elle plusieurs jours métier ?
	get multiDay(): boolean {
		const w = this.day ?? this.transfers;
		return !!w && !!w.date_to && w.date_to !== w.date;
	}

	get safesTotal(): number {
		return sumCents((this.safes ?? []).map((s) => s.balance ?? 0));
	}

	// Heure seule dans un jour métier ; jour et heure sur une période, ou pour la tranche d'après minuit avant la bascule.
	hour(dat: string): string {
		const t = parseTz(dat);
		const day = this.day?.date ?? this.transfers?.date;
		if (!t) return '';
		return t.format(this.multiDay || (day && businessDayOf(dat) !== day) ? 'DD/MM HH:mm' : 'HH:mm');
	}

	readonly stamp = dayTimeStamp;

	// « Dernier inventaire : JJ/MM/AAAA HH:mm par X » sous le coffre ; vide sans inventaire.
	lastCountLabel(s: SafeBalance): string {
		const at = countStamp(s.last_count_at, s.last_count_user_name);
		return at ? 'Dernier inventaire : ' + at : '';
	}

	shownOrders(p: DayPayment): number[] {
		return this.expandedOrders.has(p.id) ? p.order_ids : p.order_ids.slice(0, this.ordersShown);
	}

	ordersExpanded(id: number): boolean {
		return this.expandedOrders.has(id);
	}

	toggleOrders(id: number): void {
		if (!this.expandedOrders.delete(id)) this.expandedOrders.add(id);
	}

	channel(c: PaymentChannel): string {
		return c === 'COUNTER' ? '' : (this.channelLabels[c] ?? c);
	}

	methodName(p: DayPayment): string {
		return methodLabel(p, this.cashFamily);
	}

	natureLabel(n: PaymentNature): string {
		return NATURE_LABELS[n] ?? n;
	}

	get windowLabel(): string {
		const w = this.day ?? this.transfers;
		if (!w) return '';
		const a = parseTz(w.start_at), b = parseTz(w.end_at);
		return a && b ? `du ${a.format('DD/MM HH:mm')} au ${b.format('DD/MM HH:mm')}` : '';
	}

	get countLabel(): string {
		return this.day ? `${this.day.count} règlement${this.day.count > 1 ? 's' : ''}` : '';
	}
}
